import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const WARN_SECONDS = 60;
const LAST_KEY = "bgjc_last_active";
const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

export default function IdleLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(null);
  const warningRef = useRef(null);
  warningRef.current = warning;

  const remembered = !!(user?.is_staff && user?.device_id);
  const limitMs = user && !remembered ? (user.is_staff ? 90 : 20) * 60 * 1000 : 0;

  const touch = useCallback(() => localStorage.setItem(LAST_KEY, String(Date.now())), []);
  const signOut = useCallback((msg) => {
    logout();
    setWarning(null);
    if (msg) toast.info(msg);
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!user || remembered) { setWarning(null); return undefined; }
    const stored = Number(localStorage.getItem(LAST_KEY));
    if (!stored || Date.now() - stored >= limitMs) touch();

    let throttled = false;
    const onActivity = () => {
      if (warningRef.current !== null || throttled) return;
      throttled = true;
      setTimeout(() => { throttled = false; }, 5000);
      touch();
    };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = setInterval(() => {
      const last = Number(localStorage.getItem(LAST_KEY)) || Date.now();
      const left = Math.ceil((limitMs - (Date.now() - last)) / 1000);
      if (left <= 0) signOut("You were signed out after a period of inactivity.");
      else if (left <= WARN_SECONDS) setWarning(left);
      else if (warningRef.current !== null) setWarning(null);
    }, 1000);

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(tick);
    };
  }, [user, remembered, limitMs, touch, signOut]);

  if (!user || remembered || warning === null) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-sbg-burgundy/60 p-4 backdrop-blur-sm" data-testid="idle-warning">
      <div className="w-full max-w-sm rounded-3xl bg-white p-7 text-center shadow-2xl">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sbg-blush text-sbg-fuchsia"><Clock className="h-7 w-7" /></span>
        <h2 className="mt-4 font-display text-2xl font-black text-sbg-burgundy">Still there?</h2>
        <p className="mt-2 text-sm text-sbg-burgundy/70">
          For your security you'll be signed out in <span className="font-bold text-sbg-burgundy" data-testid="idle-countdown">{warning}s</span> due to inactivity.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button data-testid="idle-signout-btn" onClick={() => signOut()} className="rounded-full bg-sbg-blush px-5 py-2.5 text-sm font-bold text-sbg-raspberry">Sign out</button>
          <button data-testid="idle-stay-btn" onClick={() => { touch(); setWarning(null); }} className="rounded-full bg-sbg-fuchsia px-6 py-2.5 text-sm font-bold text-white">I'm still here</button>
        </div>
      </div>
    </div>
  );
}
